import { createSlice } from '@reduxjs/toolkit';
import { PDFConfig, PDFSection } from 'src/content/pdfBuilder/types';

/**
 * Dashboard slice initial state interface
 */
interface DashboardState {
  getOrganicSearchTrafficDetails: any;
  getGscGraphDetails: any;
  siteDetailCardLoader: any;
  organicSearchTrafficLoader: any;
  graphDetailsLoader: any;
  graphFilter: string;
  dashboardPdfLoader: any;
  getOrganicSearchTrafficDetailsPdf: any;
  getGscGraphDetailsPdf: any;
  getSiteDetailPdf: any;
  getAuditCheckersCount: any;
  filterTagList: any[];
  filterChecklistValues: any[];
  isCheckBoxedSaved: any;
  isAgencyMonitoring: any;
  offerApplied: any;
  isDowngradeToFree: any;
  gscLoader: boolean;
  allSitesData: any[];
  selectedSiteData: any;
  isSelectedSiteRemoved: any;
  isSiteSelected: any;
  isContinueAiInSight: any;
  isDetailDashboardView: any;
  aiDashboardSection: number;
  visibilityDetails: any;
  selectedAiModal: any;
  isVisibilityDetailsLoading: any;
  selectedTopic: any;
  selectedPrompt: any;
  aiVisibilityErrorData: any;
  aiVisibilityGraphData: any;
  isOpenWhatAiKnowsModal: boolean;
  selectedSeoTaskId: any;
  aiVisibilityRedisResponse: any;
  isHighlightFirstPrompt: any;
  aiAddonMessage: any;
  isAiAddonPurchased: any;
  isAiVisibilityVisit: any;
  isAiScoreView: any;
  pdfConfig: PDFConfig;
  isUpdating: boolean;
  pdfReportData: any;
  pdfReportSiteId: string | null;
  pdfReportDataLoader: boolean;
  localSeoReportData: any;
  dashboardDataFilter: {
    startDate: any;
    endDate: any;
  };
}

export const initialState: DashboardState = {
  getOrganicSearchTrafficDetails: null,
  getGscGraphDetails: null,
  siteDetailCardLoader: null,
  organicSearchTrafficLoader: null,
  graphDetailsLoader: null,
  graphFilter: 'last_7_days',
  dashboardPdfLoader: null,
  getOrganicSearchTrafficDetailsPdf: null,
  getGscGraphDetailsPdf: null,
  getSiteDetailPdf: null,
  getAuditCheckersCount: null,
  filterTagList: [],
  filterChecklistValues: [],
  isCheckBoxedSaved: null,
  isAgencyMonitoring: null,
  offerApplied: null,
  isDowngradeToFree: null,
  gscLoader: false,
  allSitesData: [],
  selectedSiteData: null,
  isSelectedSiteRemoved: null,
  isSiteSelected: null,
  isContinueAiInSight: null,
  isDetailDashboardView: null,
  aiDashboardSection: 0,
  visibilityDetails: null,
  selectedAiModal: null,
  isVisibilityDetailsLoading: null,
  selectedTopic: null,
  selectedPrompt: null,
  aiVisibilityErrorData: null,
  aiVisibilityGraphData: null,
  isOpenWhatAiKnowsModal: false,
  selectedSeoTaskId: null,
  aiVisibilityRedisResponse: null,
  isHighlightFirstPrompt: null,
  aiAddonMessage: null,
  isAiAddonPurchased: null,
  isAiVisibilityVisit: null,
  isAiScoreView: null,
  // PDF Builder Configuration
  pdfConfig: {
    timeline: 'current',
    generationMode: 'manual',
    frequency: 'week',
    includedSections: [
      PDFSection.AI_SUMMARY,
      PDFSection.OVERVIEW,
      PDFSection.TASKS,
      PDFSection.SCANNED_PAGES,
      PDFSection.AI_VISIBILITY,
      PDFSection.KEYWORD_TRACKING
    ],
    customIcon: null,
    recipients: [],
    aiSummary: {
      format: 'short',
      tone: 'professional',
      goal: []
    },
    includeAllKeywords: false,
    localSeoOptions: {
      name: null,
      includeAllKeywords: false
    },
    periodFilter: '',
    compareFilter: ''
  },
  isUpdating: false,
  pdfReportData: null,
  pdfReportSiteId: null,
  pdfReportDataLoader: false,
  localSeoReportData: null,
  dashboardDataFilter: {
    startDate: null,
    endDate: null
  }
};

/**
 * Redux slice for managing counter-related state.
 *
 * @name siteAuditSlice
 * @type {Slice}
 */
// Action functions declaration
export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setOrganicSearchTrafficDetails: (state, action) => {
      // Add a new reducer to set itemId
      state.getOrganicSearchTrafficDetails = action.payload;
    },
    setGscGraphDetails: (state, action) => {
      // Add a new reducer to set itemId
      state.getGscGraphDetails = action.payload;
    },
    setSiteDetailCardLoader: (state, action) => {
      // Add a new reducer to set itemId
      state.siteDetailCardLoader = action.payload;
    },
    setOrganicSearchTrafficLoader: (state, action) => {
      // Add a new reducer to set itemId
      state.organicSearchTrafficLoader = action.payload;
    },
    setGraphDetailsLoader: (state, action) => {
      // Add a new reducer to set itemId
      state.graphDetailsLoader = action.payload;
    },
    setGraphFilter: (state, action) => {
      // Add a new reducer to set itemId
      state.graphFilter = action.payload;
    },
    setDashboardPdfLoader: (state, action) => {
      // Add a new reducer to set itemId
      state.dashboardPdfLoader = action.payload;
    },
    setDashboardSiteDetailPdf: (state, action) => {
      state.getSiteDetailPdf = action.payload;
    },
    setDashboardAuditCheckersCount: (state, action) => {
      state.getAuditCheckersCount = action.payload;
    },
    setOrganicSearchTrafficDetailsPdf: (state, action) => {
      // Add a new reducer to set itemId
      state.getOrganicSearchTrafficDetailsPdf = action.payload;
    },
    setGscGraphDetailsPdf: (state, action) => {
      // Add a new reducer to set itemId
      state.getGscGraphDetailsPdf = action.payload;
    },
    setFilterTagList: (state, action) => {
      // Add a new reducer to set itemId
      state.filterTagList = action.payload;
    },
    setFilterChecklistValues: (state, action) => {
      // Add a new reducer to set itemId
      state.filterChecklistValues = action.payload;
    },
    setIsCheckBoxedSaved: (state, action) => {
      // Add a new reducer to set itemId
      state.isCheckBoxedSaved = action.payload;
    },
    setIsAgencyMonitoring: (state, action) => {
      // Add a new reducer to set itemId
      state.isAgencyMonitoring = action.payload;
    },
    setOfferApplied: (state, action) => {
      // Add a new reducer to set itemId
      state.offerApplied = action.payload;
    },
    setIsDowngradeToFree: (state, action) => {
      // Add a new reducer to set itemId
      state.isDowngradeToFree = action.payload;
    },
    setIsGscLoader: (state, action) => {
      // Add a new reducer to set itemId
      state.gscLoader = action.payload;
    },
    setAllSitesData: (state, action) => {
      // Add a new reducer to set itemId
      state.allSitesData = action.payload;
    },
    setSelectedSiteData: (state, action) => {
      // Add a new reducer to set itemId
      state.selectedSiteData = action.payload;
    },
    setIsSelectedSiteRemoved: (state, action) => {
      // Add a new reducer to set itemId
      state.isSelectedSiteRemoved = action.payload;
    },
    setIsSiteSelected: (state, action) => {
      // Add a new reducer to set itemId
      state.isSiteSelected = action.payload;
    },
    setIsContinueAiInSight: (state, action) => {
      // Add a new reducer to set itemId
      state.isContinueAiInSight = action.payload;
    },
    setIsDetailDashboardView: (state, action) => {
      // Add a new reducer to set itemId
      state.isDetailDashboardView = action.payload;
    },
    setAiDashboardSection: (state, action) => {
      // Add a new reducer to set itemId
      state.aiDashboardSection = action.payload;
    },
    setVisibilityDetails: (state, action) => {
      // Add a new reducer to set itemId
      state.visibilityDetails = action.payload;
    },
    setSelectedAiModal: (state, action) => {
      // Add a new reducer to set itemId
      state.selectedAiModal = action.payload;
    },
    setIsVisibilityDetailsLoading: (state, action) => {
      // Add a new reducer to set itemId
      state.isVisibilityDetailsLoading = action.payload;
    },
    setSelectedTopic: (state, action) => {
      // Add a new reducer to set itemId
      state.selectedTopic = action.payload;
    },
    setSelectedPrompt: (state, action) => {
      // Add a new reducer to set itemId
      state.selectedPrompt = action.payload;
    },
    setAiVisibilityErrorData: (state, action) => {
      // Add a new reducer to set itemId
      state.aiVisibilityErrorData = action.payload;
    },
    setAiVisibilityGraphData: (state, action) => {
      // Add a new reducer to set itemId
      state.aiVisibilityGraphData = action.payload;
    },
    setOpenWhatAiKnowsModal: (state, action) => {
      // Add a new reducer to set itemId
      state.isOpenWhatAiKnowsModal = action.payload;
    },
    setSelectedSeoTaskId: (state, action) => {
      // Add a new reducer to set itemId
      state.selectedSeoTaskId = action.payload;
    },
    setAiVisibilityRedisResponse: (state, action) => {
      // Add a new reducer to set itemId
      state.aiVisibilityRedisResponse = action.payload;
    },
    setIsHighlightFirstPrompt: (state, action) => {
      // Add a new reducer to set itemId
      state.isHighlightFirstPrompt = action.payload;
    },
    setAiAddonMessage: (state, action) => {
      // Add a new reducer to set itemId
      state.aiAddonMessage = action.payload;
    },
    setIsAiAddonPurchased: (state, action) => {
      // Add a new reducer to set itemId
      state.isAiAddonPurchased = action.payload;
    },
    setIsAiVisibilityVisit: (state, action) => {
      // Add a new reducer to set itemId
      state.isAiVisibilityVisit = action.payload;
    },
    setIsAiScoreView: (state, action) => {
      // Add a new reducer to set itemId
      state.isAiScoreView = action.payload;
    },
    setDashboardDataFilter: (state, action) => {
      // Add a new reducer to set itemId
      state.dashboardDataFilter = action.payload;
    },
    setPdfReportData: (state, action) => {
      state.pdfReportData = action.payload;
    },
    setPdfReportDataLoader: (state, action) => {
      state.pdfReportDataLoader = action.payload;
    },
    setPdfReportSiteId: (state, action) => {
      state.pdfReportSiteId = action.payload;
    },
    setLocalSeoReportData: (state, action) => {
      state.localSeoReportData = action.payload;
    },
    /**
     * Update PDF Configuration
     * Handles partial updates to the PDF builder configuration
     * This allows for flexible, scalable updates without replacing the entire config
     *
     * @param state - Current dashboard state
     * @param action - Action with partial PDF config payload
     *
     * @example
     * dispatch(updatePDFConfig({ timeline: 'compare' }))
     * dispatch(updatePDFConfig({ includedSections: ['overview', 'tasks'] }))
     */
    setIsUpdating: (state, action: { payload: boolean }) => {
      state.isUpdating = action.payload;
    },
    setPDFCustomIcon: (state, action: { payload: string | null }) => {
      state.pdfConfig.customIcon = action.payload;
    },
    setPDFRecipients: (state, action: { payload: string[] }) => {
      state.pdfConfig.recipients = action.payload;
    },
    updatePDFConfig: (state, action) => {
      const { aiSummary, localSeoOptions, ...rest } = action.payload;
      state.pdfConfig = {
        ...state.pdfConfig,
        ...rest,
        aiSummary: action.payload.aiSummary
          ? {
              ...state.pdfConfig.aiSummary,
              ...action.payload.aiSummary
            }
          : state.pdfConfig.aiSummary,
        localSeoOptions: action.payload.localSeoOptions
          ? {
              ...state.pdfConfig.localSeoOptions,
              ...action.payload.localSeoOptions
            }
          : state.pdfConfig.localSeoOptions
      };
    },
    resetPdfConfig: (state) => {
      state.pdfConfig = initialState.pdfConfig;
      state.isUpdating = initialState.isUpdating;
      state.pdfReportData = initialState.pdfReportData;
      state.pdfReportSiteId = initialState.pdfReportSiteId;
      state.pdfReportDataLoader = initialState.pdfReportDataLoader;
      state.localSeoReportData = initialState.localSeoReportData;
    },
    /**
     * Clean up and reset the state to its initial values.
     *
     * @function
     * @name cleanupInitialState
     */
    cleanupKeywordInitialState: () => initialState
  }
});

export const {
  setOrganicSearchTrafficDetails,
  setGscGraphDetails,
  setSiteDetailCardLoader,
  setOrganicSearchTrafficLoader,
  setGraphDetailsLoader,
  setGraphFilter,
  setDashboardPdfLoader,
  setDashboardSiteDetailPdf,
  setDashboardAuditCheckersCount,
  setOrganicSearchTrafficDetailsPdf,
  setGscGraphDetailsPdf,
  setFilterTagList,
  setFilterChecklistValues,
  setIsCheckBoxedSaved,
  setIsAgencyMonitoring,
  setOfferApplied,
  setIsDowngradeToFree,
  setIsGscLoader,
  setAllSitesData,
  setSelectedSiteData,
  setIsSelectedSiteRemoved,
  setIsSiteSelected,
  setIsContinueAiInSight,
  setIsDetailDashboardView,
  setAiDashboardSection,
  setVisibilityDetails,
  setSelectedAiModal,
  setIsVisibilityDetailsLoading,
  setSelectedTopic,
  setSelectedPrompt,
  setAiVisibilityErrorData,
  setAiVisibilityGraphData,
  setOpenWhatAiKnowsModal,
  setSelectedSeoTaskId,
  setAiVisibilityRedisResponse,
  setIsHighlightFirstPrompt,
  setAiAddonMessage,
  setIsAiAddonPurchased,
  setIsAiVisibilityVisit,
  setIsAiScoreView,
  setIsUpdating,
  setPDFCustomIcon,
  setPDFRecipients,
  updatePDFConfig,
  setPdfReportData,
  setPdfReportDataLoader,
  setPdfReportSiteId,
  setLocalSeoReportData,
  cleanupKeywordInitialState,
  setDashboardDataFilter,
  resetPdfConfig
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
